import React, { useCallback, useEffect, useMemo } from 'react';
import debounce from 'lodash.debounce';
import { TabsContext } from './context';
import { useDidUpdateEffect, useInput, usePrevious } from '../hooks';
import { useTabsRefs } from './hooks/useTabsRefs';
import { useTabsScroll } from './hooks/useTabsScroll';

var Tabs = function Tabs(_ref) {
  var defaultSelected = _ref.defaultSelected,
      selectedProp = _ref.selected,
      onSelect = _ref.onSelect,
      children = _ref.children;

  var _useInput = useInput(selectedProp, defaultSelected, onSelect),
      selected = _useInput[0],
      setSelected = _useInput[1];

  var selectedPrevious = usePrevious(selected, true);

  var _useTabsRefs = useTabsRefs(),
      refList = _useTabsRefs.refList,
      refs = _useTabsRefs.refs,
      refsInner = _useTabsRefs.refsInner,
      tabNames = _useTabsRefs.tabNames,
      onLineReset = _useTabsRefs.onLineReset,
      focus = _useTabsRefs.focus,
      focusNext = _useTabsRefs.focusNext,
      focusPrevious = _useTabsRefs.focusPrevious,
      focusFirst = _useTabsRefs.focusFirst,
      focusLast = _useTabsRefs.focusLast;

  var scrollToTab = useTabsScroll(refs, refsInner, refList, tabNames); // scrolling to current tab when it's changing

  var scrollToCurrentTab = useCallback(debounce(function (smooth) {
    if (smooth === void 0) {
      smooth = true;
    }

    if (refList.current) {
      scrollToTab.current(selected, smooth);
    }
  }, 100), [selected]); // handle scroll container resize

  var resize = useCallback(debounce(function (withTransition) {
    if (withTransition === void 0) {
      withTransition = false;
    }

    onLineReset.current == null ? void 0 : onLineReset.current(withTransition);
    scrollToCurrentTab(withTransition);
  }, 100), [scrollToCurrentTab]); // initial scroll to selected tab

  useEffect(function () {
    scrollToCurrentTab(false);
  }, []); // scroll on selected change

  useDidUpdateEffect(function () {
    if (refs.current[selected]) {
      scrollToTab.current(selected);
    }
  }, [selected]);
  var contextValue = useMemo(function () {
    return {
      select: setSelected,
      focus: focus,
      focusNext: focusNext,
      focusPrevious: focusPrevious,
      focusFirst: focusFirst,
      focusLast: focusLast,
      refs: refs,
      refsInner: refsInner,
      refList: refList,
      tabNames: tabNames,
      resize: resize,
      selected: selected,
      selectedPrevious: selectedPrevious,
      onLineReset: onLineReset
    };
  }, [resize, selected, selectedPrevious]);
  return /*#__PURE__*/React.createElement(TabsContext.Provider, {
    value: contextValue
  }, children);
};

export default Tabs;